import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DB } from './data/database';
import { ROLES } from './data/constants';
import { LanguageProvider, useLanguage } from './translations/LanguageContext';
import Login from './components/Login';
import DetailModal from './components/modals/DetailModal';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import Dashboard from './components/Dashboard';
import Inventory from './components/Inventory';
import Customers from './components/Customers';
import MyStock from './components/MyStock';
import Containers from './components/Containers';
import Requests from './components/Requests';
import Scan from './components/Scan';
import Staff from './components/Staff';
import Settings from './components/Settings';
import AuditLog from './components/AuditLog';
import Pricing from './components/Pricing';
import SelectionOrders from './components/SelectionOrders';
import Invoices from './components/Invoices';
import PriceQuotes from './components/PriceQuotes';
import Payments from './components/Payments';
import Tasks from './components/Tasks';
import CutPlans from './components/CutPlans';
import YardPricing from './components/YardPricing';
import YardSalesHistory from './components/YardSalesHistory';
import WalkInCustomers from './components/WalkInCustomers';
import QRScanner from './components/QRScanner';
import QRActionMenu from './components/QRActionMenu';
import PublicUnitView from './components/PublicUnitView';
import WebsiteApp from './components/website/WebsiteApp';
import CMS from './components/cms/CMS';
import Inquiries from './components/Inquiries';
import TradeApplications from './components/TradeApplications';
import WebsiteCars from './components/WebsiteCars';
import BulkPartsUpload from './components/BulkPartsUpload';
import MarketPrices from './components/MarketPrices';
import YardMap from './components/YardMap';
import { getPersistedFilter } from './components/CustomerFilter';
import { isPushSupported, isPushDismissed, isPushGranted, dismissPush, registerPush } from './utils/pushNotifications';
import { startPresence, stopPresence } from './utils/presence';

const DEFAULT_LANGS = ['en', 'ar', 'tr', 'ru', 'ka', 'fr', 'ja', 'sw', 'pt', 'es', 'ur', 'zh'];
const SESSION_KEY   = 'bigstar_session';
const TIMEOUT_MS    = 20 * 60 * 1000;


function App() {
  // App consumes useLanguage so it re-renders when language changes
  useLanguage();
  const [dbReady, setDbReady]           = useState(false);
  const [user, setUser]                 = useState(null);
  const [timedOut, setTimedOut]         = useState(false);
  const [page, setPageRaw]              = useState(() => {
    try {
      const s = localStorage.getItem(SESSION_KEY);
      return s ? (JSON.parse(s).page || 'dashboard') : 'dashboard';
    } catch { return 'dashboard'; }
  });
  const [currency, setCurrency]         = useState('JPY');
  const [sidebarOpen, setSidebarOpen]   = useState(false);
  const [custFilter, setCustFilter]     = useState(() => getPersistedFilter());
  const [activeLangs, setActiveLangs]   = useState(() => DB.get('activeLanguages') || DEFAULT_LANGS);
  const [deepLinkItem, setDeepLinkItem] = useState(null);
  const [showFabScanner, setShowFabScanner] = useState(false);
  const [fabExpanded, setFabExpanded]       = useState(false);
  const [fabScannedValue, setFabScannedValue] = useState(null);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const inactivityTimer = useRef(null);

  const setPage = (p) => {
    setPageRaw(p);
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (raw) {
        const session = JSON.parse(raw);
        localStorage.setItem(SESSION_KEY, JSON.stringify({ ...session, page: p }));
      }
    } catch {}
  };

  const deepLinkSku = useRef(
    new URLSearchParams(window.location.search).get('unit')
    || new URLSearchParams(window.location.search).get('u')
    || null
  );

  useEffect(() => {
    DB.init().then(() => {
      try {
        const raw = localStorage.getItem(SESSION_KEY);
        if (raw) {
          const { userData, lastActivity } = JSON.parse(raw);
          if (Date.now() - lastActivity < TIMEOUT_MS) {
            setUser(userData);
            startPresence(userData);
          } else {
            localStorage.removeItem(SESSION_KEY);
          }
        }
      } catch {
        localStorage.removeItem(SESSION_KEY);
      }
      setDbReady(true);
    });
  }, []);

  useEffect(() => {
    if (!dbReady || !user) return;
    const now = Date.now();
    const today = new Date().toISOString().split('T')[0];
    const checkKey = `bigstar_overdue_check_${today}`;
    if (localStorage.getItem(checkKey)) return;
    localStorage.setItem(checkKey, '1');

    const invoices = DB.get('invoices') || [];
    const { Notifications } = require('./data/notifications');
    invoices.forEach(inv => {
      (Array.isArray(inv.paymentMilestones) ? inv.paymentMilestones : []).forEach(m => {
        if (m.status === 'paid' || !m.due_date) return;
        const due = new Date(m.due_date).getTime();
        const daysUntil = Math.floor((due - now) / 86400000);
        const existingKey = `reminder_${inv.id}_${m.milestone || m.label}_${daysUntil}`;
        if (localStorage.getItem(existingKey)) return;

        const cur = inv.currency || 'USD';
        const amt = (m.amount || 0).toLocaleString();

        const params = { name: inv.customerName || '', currency: cur, amount: amt, number: inv.invoiceNumber || '', days: Math.abs(daysUntil), date: m.due_date || '' };

        if (daysUntil === 3 || daysUntil === 1) {
          Notifications.addForCustomer('payment_received', 'payment_reminder', { ...params, _fallbackTitle: 'Payment Reminder', _fallbackMessage: `Payment of ${cur} ${amt} for ${inv.invoiceNumber} due in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}` }, inv.customerId);
          localStorage.setItem(existingKey, '1');
        } else if (daysUntil === 0) {
          Notifications.addForCustomer('payment_received', 'payment_overdue', { ...params, days: 0, _fallbackTitle: 'Payment Due Today', _fallbackMessage: `Payment of ${cur} ${amt} for ${inv.invoiceNumber} is due today` }, inv.customerId);
          localStorage.setItem(existingKey, '1');
        } else if (daysUntil === -3) {
          Notifications.addForCustomer('payment_received', 'payment_overdue', { ...params, _fallbackTitle: 'Payment Overdue', _fallbackMessage: `Payment overdue 3 days` }, inv.customerId);
          Notifications.add('payment_received', 'Payment Overdue', `${inv.customerName} — ${cur} ${amt} overdue 3 days — ${inv.invoiceNumber}`, {});
          localStorage.setItem(existingKey, '1');
        } else if (daysUntil === -7) {
          Notifications.addForCustomer('payment_received', 'payment_overdue', { ...params, _fallbackTitle: 'URGENT: Payment Overdue', _fallbackMessage: `Payment 7 days overdue` }, inv.customerId);
          Notifications.add('payment_received', 'URGENT: Payment Overdue 7 Days', `${inv.customerName} owes ${cur} ${amt} — 7 days past due — ${inv.invoiceNumber}`, {});
          localStorage.setItem(existingKey, '1');
        } else if (daysUntil < 0 && daysUntil >= -1) {
          Notifications.addForCustomer('payment_received', 'payment_overdue', { ...params, _fallbackTitle: 'Payment Overdue', _fallbackMessage: `Payment overdue` }, inv.customerId);
          Notifications.add('payment_received', 'Payment Overdue', `${inv.customerName} — ${cur} ${amt} overdue for ${inv.invoiceNumber}`, {});
          localStorage.setItem(existingKey, '1');
        }
      });
    });
  }, [dbReady, user]);

  useEffect(() => {
    if (!dbReady || !user || !deepLinkSku.current) return;
    const sku = deepLinkSku.current;
    deepLinkSku.current = null;
    const all = [
      ...(DB.get('vehicles')   || []).map(i => ({ ...i, _dbType: 'vehicles' })),
      ...(DB.get('engines')    || []).map(i => ({ ...i, _dbType: 'engines'  })),
      ...(DB.get('spareParts') || []).map(i => ({ ...i, _dbType: 'parts'    })),
    ];
    const found = all.find(i => i.sku === sku);
    if (found) {
      setDeepLinkItem(found);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [dbReady, user]);

  const resetTimer = useCallback(() => {
    if (!user) return;
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (raw) {
        const session = JSON.parse(raw);
        localStorage.setItem(SESSION_KEY, JSON.stringify({ ...session, lastActivity: Date.now() }));
      }
    } catch {}
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      localStorage.removeItem(SESSION_KEY);
      setUser(null);
      setTimedOut(true);
    }, TIMEOUT_MS);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      clearTimeout(inactivityTimer.current);
    };
  }, [user, resetTimer]);

  useEffect(() => {
    const handler = (e) => {
      const code = e.detail;
      setActiveLangs(prev => {
        if (prev.includes(code)) return prev;
        const updated = [...prev, code];
        DB.set('activeLanguages', updated);
        return updated;
      });
    };
    window.addEventListener('langAdded', handler);
    return () => window.removeEventListener('langAdded', handler);
  }, []);

  useEffect(() => {
    if (user?.currency) setCurrency(user.currency);
  }, [user]);

  const handleLogin = (userData) => {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ userData, lastActivity: Date.now() }));
    setUser(userData);
    setTimedOut(false);
    startPresence(userData);
    // web_content role goes directly to CMS
    setPage(userData.role === 'web_content' ? 'cms' : 'dashboard');
    // Show push prompt if supported and not dismissed/granted
    if (isPushSupported() && !isPushGranted() && !isPushDismissed()) {
      setTimeout(() => setShowPushPrompt(true), 3000);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(SESSION_KEY);
    clearTimeout(inactivityTimer.current);
    stopPresence();
    setUser(null);
    setTimedOut(true);
    setPage('dashboard');
  };

  const handleDeepLinkUpdate = (updatedItem) => {
    const key = updatedItem._dbType === 'vehicles' ? 'vehicles'
              : updatedItem._dbType === 'engines'  ? 'engines'
              : 'spareParts';
    const list = DB.get(key) || [];
    DB.set(key, list.map(i => i.id === updatedItem.id ? updatedItem : i));
    setDeepLinkItem(updatedItem);
  };

  if (!dbReady) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0d1117' }}>
        <div style={{ textAlign: 'center', color: '#8b949e' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>★</div>
          <div style={{ fontSize: 16 }}>Loading BIG STAR...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    // Check for public unit view (QR code scanned by non-logged-in person)
    const publicSku = new URLSearchParams(window.location.search).get('u') || new URLSearchParams(window.location.search).get('unit');
    if (publicSku && !timedOut) {
      const allU = [
        ...(DB.get('vehicles') || []).map(i => ({ ...i, _dbType: 'vehicles' })),
        ...(DB.get('engines') || []).map(i => ({ ...i, _dbType: 'engines' })),
        ...(DB.get('spareParts') || []).map(i => ({ ...i, _dbType: 'parts' })),
      ];
      const found = allU.find(i => i.sku === publicSku);
      if (found) {
        return <PublicUnitView unit={found} onLogin={() => {
          deepLinkSku.current = publicSku;
          setTimedOut(true); // triggers re-render, falls through to Login below
        }} />;
      }
    }
    // Website pages for non-logged-in visitors
    const wsPage = new URLSearchParams(window.location.search).get('page') || 'website';
    if (wsPage === 'login' || timedOut) {
      return <WebsiteApp initialPage="website-login" onLogin={() => setTimedOut(true)} loginHandler={handleLogin} timedOut={timedOut} />;
    }
    return <WebsiteApp initialPage={wsPage} onLogin={() => setTimedOut(true)} />;
  }

  const role = ROLES[user.role] || {};

  // BUG 3 FIX: ?preview=true shows the website even when logged in
  const previewParam = new URLSearchParams(window.location.search).get('preview');
  if (previewParam === 'true') {
    return <WebsiteApp initialPage="website" onLogin={() => {}} />;
  }

  // If opened via ?portal=cms (new tab from sidebar), go straight to CMS
  const portalParam = new URLSearchParams(window.location.search).get('portal');
  if (portalParam === 'cms' && (user.role === 'admin' || user.role === 'head_admin' || user.role === 'web_content')) {
    return <CMS user={user} />;
  }

  // web_content role always sees CMS
  if (role.isCMS) {
    return <CMS user={user} />;
  }

  const renderPage = () => {
    switch (page) {
      case 'dashboard':       return <Dashboard user={user} currency={currency} setPage={setPage} custFilter={custFilter} setCustFilter={setCustFilter} />;
      case 'inventory':       return <Inventory user={user} currency={currency} />;
      case 'customers':       return <Customers user={user} currency={currency} />;
      case 'myStock':         return <MyStock user={user} currency={currency} />;
      case 'containers':      return <Containers user={user} currency={currency} custFilter={custFilter} setCustFilter={setCustFilter} />;
      case 'invoices':        return <Invoices user={user} currency={currency} custFilter={custFilter} setCustFilter={setCustFilter} />;
      case 'payments':        return <Payments user={user} custFilter={custFilter} setCustFilter={setCustFilter} />;
      case 'priceQuotes':     return <PriceQuotes user={user} custFilter={custFilter} setCustFilter={setCustFilter} />;
      case 'selectionOrders': return <SelectionOrders user={user} custFilter={custFilter} setCustFilter={setCustFilter} />;
      case 'requests':        return <Requests user={user} setPage={setPage} />;
      case 'scan':            return <Scan user={user} />;
      case 'pricing':         return <Pricing user={user} currency={currency} setPage={setPage} />;
      case 'employees':       return <Staff user={user} />;
      case 'tasks':           return <Tasks user={user} />;
      case 'cutPlans':        return <CutPlans user={user} setPage={setPage} />;
      case 'yardPricing':     return <YardPricing user={user} setPage={setPage} />;
      case 'yardHistory':     return <YardSalesHistory user={user} />;
      case 'walkInCustomers': return <WalkInCustomers user={user} />;
      case 'settings':        return <Settings user={user} onUpdateUser={updated => setUser(updated)} />;
      case 'auditLog':        return <AuditLog user={user} />;
      case 'inquiries':       return <Inquiries user={user} />;
      case 'tradeApplications': return <TradeApplications user={user} />;
      case 'websiteCars':       return <WebsiteCars user={user} />;
      case 'bulkParts':         return <BulkPartsUpload user={user} />;
      case 'marketPrices':      return <MarketPrices user={user} />;
      case 'yardMap':           return <YardMap user={user} />;
      case 'cms':             return <CMS user={user} />;
      default:                return <Dashboard user={user} currency={currency} />;
    }
  };

  return (
    <>
      <div className={`app ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <Sidebar
          user={user}
          role={role}
          page={page}
          setPage={setPage}
          onLogout={handleLogout}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
        <main className="main">
          <Topbar
            page={page}
            user={user}
            currency={currency}
            setCurrency={setCurrency}
            onMenuClick={() => setSidebarOpen(true)}
          />
          <div className="page-content">
            {renderPage()}
          </div>
        </main>
      </div>

      {/* Push notification permission prompt */}
      {showPushPrompt && (
        <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 9000, padding: '16px 24px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', gap: 16, maxWidth: 480 }}>
          <span style={{ fontSize: 28 }}>🔔</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>Stay Updated</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>Get notifications about invoices, shipments, and payments even when you're not on the site.</div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button className="btn btn-primary btn-sm" onClick={async () => {
              const result = await registerPush();
              if (result?.subscription && user) {
                const key = role.isCustomer ? 'customers' : 'users';
                const list = DB.get(key) || [];
                DB.set(key, list.map(u => u.id === user.id ? { ...u, push_subscription: result.subscription } : u));
              }
              setShowPushPrompt(false);
            }}>Allow</button>
            <button className="btn btn-secondary btn-sm" onClick={() => { dismissPush(); setShowPushPrompt(false); }}>Not Now</button>
          </div>
        </div>
      )}

      {deepLinkItem && (
        <DetailModal
          item={deepLinkItem}
          type={deepLinkItem._dbType}
          onClose={() => setDeepLinkItem(null)}
          currency={currency}
          canPrice={role.canPrice}
          isCustomer={role.isCustomer}
          onUpdate={handleDeepLinkUpdate}
          user={user}
        />
      )}

      {/* FAB — QR Scanner (collapsible) */}
      {page !== 'scan' && ['inventory','containers','cutPlans','myStock','pricing','priceQuotes'].includes(page) && (
        fabExpanded ? (
          <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 100, display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="qr-fab" onClick={() => setShowFabScanner(true)} title="Scan QR Code">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                <line x1="14" y1="14" x2="14" y2="14.01"/><line x1="21" y1="14" x2="21" y2="14.01"/><line x1="14" y1="21" x2="14" y2="21.01"/><line x1="21" y1="21" x2="21" y2="21.01"/><line x1="17" y1="17" x2="17" y2="17.01"/>
              </svg>
            </button>
            <button onClick={() => setFabExpanded(false)} style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Collapse">✕</button>
          </div>
        ) : (
          <button onClick={() => setFabExpanded(true)} style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 100, width: 36, height: 36, borderRadius: '50%', background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text3)', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.6, transition: 'opacity 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '0.6'; }}
            title="Open QR Scanner">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
          </button>
        )
      )}

      {/* FAB Scanner overlay */}
      {showFabScanner && (
        <QRScanner
          onScan={(value) => {
            setShowFabScanner(false);
            const cleaned = value.replace(/^https?:\/\/[^?]*\?u(?:nit)?=/i, '').replace('BIGSTAR:', '').trim();
            setFabScannedValue(cleaned);
          }}
          onClose={() => setShowFabScanner(false)}
        />
      )}

      {/* FAB Action Menu */}
      {fabScannedValue && (
        <QRActionMenu
          scannedValue={fabScannedValue}
          user={user}
          onClose={() => setFabScannedValue(null)}
          onNavigate={(type, item) => {
            setFabScannedValue(null);
            if (type === 'details' && item) {
              setDeepLinkItem({ ...item, _dbType: item._dbKey === 'vehicles' ? 'vehicles' : item._dbKey === 'engines' ? 'engines' : 'parts' });
            }
          }}
        />
      )}
    </>
  );
}

function AppWithLanguage() {
  return (
    <LanguageProvider>
      <App />
    </LanguageProvider>
  );
}

export default AppWithLanguage;
